import { createMachine, assign, setup } from "xstate";
import { ExemptionQuestions } from "utils/types/exemption-questions";
import { commonGuards } from "./_commonGuards";
import moment from "moment";

export const floridaExemptionQuestionFlowMachine = setup({
	types: {
		context: {} as ExemptionQuestions.FlowContext,
		events: {} as ExemptionQuestions.FlowEvent,
		input: {} as Partial<ExemptionQuestions.FlowContext>,
	},
	actions: {
		updateContext: assign(({ context, event }) => {
			if ("context" in event) {
				return {
					...context,
					...event.context,
				};
			}
			return context;
		}),
	},
	guards: {},
	delays: {},
}).createMachine({
	id: "FLORIDA_EXEMPTION",
	context: ({ input }) => ({ ...input }) as ExemptionQuestions.FlowContext,
	initial: "IDLE",
	states: {
		IDLE: {},
		INIT: {
			always: [
				{
					target: "INTRO",
					guard: ({ context, event }) => {
						const url = new URL(window.location.href);
						return (
							context.primaryResidenceValue === undefined &&
							url.searchParams.get("q") !== "primaryResidence"
						);
					},
				},
				{
					target: "PRIMARY_RESIDENCE",
					guard: ({ context, event }) =>
						context.primaryResidenceValue === undefined,
				},
				{
					target: "CURRENTLY_RECEIVING",
					guard: ({ context, event }) => {
						return context.currentlyReceivingValue === undefined;
					},
				},
				{
					target: "PREVIOUS_RESIDENCE",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						[
							context.previousAddressValue,
							context.previousCityValue,
							context.previousStateValue,
							context.previousZipcodeValue,
							context.previousMoveOutDateValue,
						].some(value => value === undefined),
				},
				{
					target: "PORTABILITY",
					guard: ({ context, event }) => {
						if (!commonGuards.currentlyReceivingNoOrNotSure(context))
							return false;
						if (
							context.specialFieldsValue?.previous_address_homestead ===
							undefined
						)
							return true;
						if (
							context.specialFieldsValue?.previous_address_homestead === true &&
							context.specialFieldsValue?.previous_address_file_portability ===
								undefined
						)
							return true;
						if (
							context.specialFieldsValue?.previous_address_homestead === true &&
							context.specialFieldsValue?.previous_address_file_portability ===
								true &&
							!context.previousMoveOutDateValue
						)
							return true;
						return false;
					},
				},
				{
					target: "US_CITIZEN",
					guard: ({ context, event }) => {
						return (
							commonGuards.currentlyReceivingNoOrNotSure(context) &&
							context.specialFieldsValue?.us_citizen === undefined
						);
					},
				},
				{
					target: "DOB",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.dobValue === undefined,
				},
				{
					target: "RARE_SITUATIONS",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						[
							context.hasDisability === true,
							context.specialFieldsValue?.is_widow === true,
							context.specialFieldsValue?.is_blind === true,
							context.specialFieldsValue
								?.is_disabled_first_responder_or_surviving_spouse_of_a_first_responder ===
								true,
							context.specialFieldsValue
								?.is_surviving_spouse_of_a_first_responder_who_died_in_the_line_of_duty ===
								true,
							context.specialFieldsValue
								?.is_a_disabled_veteran_aged_65_or_older_or_a_surviving_spouse_of_a_disabled_veteran ===
								true,
							context.specialFieldsValue
								?.is_a_disabled_veteran_confined_to_a_wheelchair === true,
							context.specialFieldsValue
								?.is_a_totally_and_permanently_disabled_veteran_or_a_surviving_spouse_of_such_a_veteran ===
								true,
							context.specialFieldsValue
								?.is_a_surviving_spouse_of_a_veteran_who_died_while_on_active_duty ===
								true,
							context.specialFieldsValue?.is_active_duty_personnel ===
								undefined,
							context.noneOfTheAbove === undefined,
						].some(value => value === true),
				},
				{
					target: "MOVE_IN_DATE",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.moveInDateValue === undefined,
				},
				{
					target: "SSN_TIN",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.owner1HasEnteredSsnTin === undefined,
				},
				{
					target: "VEHICLE_REGISTRATIONS",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.specialFieldsValue?.vehicle_registrations === undefined,
				},
				{
					target: "CURRENT_EMPLOYER",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.specialFieldsValue?.current_employer === undefined,
				},
				{
					target: "PREVIOUS_TAX_RETURN_ADDRESS",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						[
							context.specialFieldsValue?.previous_tax_return_address?.address,
							context.specialFieldsValue?.previous_tax_return_address?.city,
							context.specialFieldsValue?.previous_tax_return_address?.state,
							context.specialFieldsValue?.previous_tax_return_address?.zip_code,
						].some(value => value === undefined),
				},
				{
					target: "MARITAL_STATUS",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.maritalStatusValue === undefined,
				},
				{
					target: "OWNERS",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.ownersValue === undefined,
				},
				{
					target: "ADDITIONAL_OWNER",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						!commonGuards.isSoleOwner(context) &&
						context.owner2HasEnteredSsnTin === undefined &&
						[
							context.additionalOwnerFirstNameValue === undefined,
							context.additionalOwnerLastNameValue === undefined,
							context.additionalOwnerDobValue === undefined,
						].some(value => value === true),
				},
				{
					target: "OWNERSHIP_PERCENT",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						!commonGuards.isSoleOwner(context) &&
						context.ownerOneValue === undefined &&
						context.ownerTwoValue === undefined,
				},
				{
					target: "DL_UPLOAD",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						!context.ownerHasUploadedDlFile,
				},
				{
					target: "DL2_UPLOAD",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						!commonGuards.isSoleOwner(context) &&
						!!context.ownerHasUploadedDlFile &&
						!context.owner2HasUploadedDlFile,
				},
				{
					target: "AGREEMENT",
					guard: ({ context, event }) =>
						[
							context.primaryResidenceValue === true,
							context.currentlyReceivingValue === "no" ||
								context.currentlyReceivingValue === "notSure",
						].every(value => value === true),
				},
			],
		},
		INTRO: {
			entry: [
				assign({
					exemptionsProgress: context => 0,
				}),
			],
			on: {
				next: {
					target: "PRIMARY_RESIDENCE",
				},
			},
		},
		PRIMARY_RESIDENCE: {
			entry: [
				assign({
					exemptionsProgress: context => 0,
				}),
			],
			on: {
				back: {
					target: "INTRO",
					actions: assign({
						primaryResidenceValue: context => undefined,
					}),
				},
				next: [
					{
						target: "CURRENTLY_RECEIVING",
						guard: ({ context, event }) =>
							commonGuards.isPrimaryResidence(context),
					},
					{
						target: "NON_AGREEMENT_CONFIRMATION",
						guard: ({ context, event }) =>
							commonGuards.isNotPrimaryResidence(context),
					},
				],
			},
		},
		NON_AGREEMENT_CONFIRMATION: {
			entry: [
				assign({
					exemptionsProgress: context => 100,
				}),
			],
		},
		CURRENTLY_RECEIVING: {
			entry: [
				assign({
					exemptionsProgress: context => (1 / 16) * 100,
				}),
			],
			on: {
				back: {
					target: "PRIMARY_RESIDENCE",
					actions: assign({
						currentlyReceivingValue: context => undefined,
					}),
				},
				next: [
					{
						target: "PREVIOUS_RESIDENCE",
						guard: ({ context, event }) =>
							commonGuards.currentlyReceivingNoOrNotSure(context),
					},
					{
						target: "NON_AGREEMENT_CONFIRMATION",
						guard: ({ context, event }) =>
							!commonGuards.currentlyReceivingNoOrNotSure(context),
					},
				],
			},
		},
		PREVIOUS_RESIDENCE: {
			entry: [
				assign({
					exemptionsProgress: context => (2 / 16) * 100,
				}),
			],
			on: {
				back: {
					target: "CURRENTLY_RECEIVING",
					actions: assign({
						previousAddressValue: context => undefined,
						previousAddress2Value: context => undefined,
						previousCityValue: context => undefined,
						previousStateValue: context => undefined,
						previousZipcodeValue: context => undefined,
						previousMoveOutDateValue: context => undefined,
					}),
				},
				next: {
					target: "PORTABILITY",
				},
			},
		},
		PORTABILITY: {
			entry: [
				assign({
					exemptionsProgress: context => (3 / 16) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "PREVIOUS_RESIDENCE",
						guard: ({ context, event }) =>
							commonGuards.currentlyReceivingNoOrNotSure(context),
						actions: assign({
							previousMoveOutDateValue: context => undefined,
							specialFieldsValue: ({ context, event }) => {
								return {
									...context.specialFieldsValue,
									previous_address_homestead: undefined,
									previous_address_file_portability: undefined,
								};
							},
						}),
					},
				],
				next: [
					{
						target: "US_CITIZEN",
					},
				],
			},
		},
		US_CITIZEN: {
			entry: [
				assign({
					exemptionsProgress: context => (4 / 16) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "PORTABILITY",
						guard: ({ context, event }) =>
							commonGuards.currentlyReceivingNoOrNotSure(context),
						actions: assign({
							specialFieldsValue: ({ context, event }) => {
								return {
									...context.specialFieldsValue,
									us_citizen: undefined,
								};
							},
						}),
					},
				],
				next: [
					{
						target: "DOB",
						guard: ({ context, event }) =>
							context.specialFieldsValue?.us_citizen === true,
					},
					{
						target: "NON_AGREEMENT_CONFIRMATION",
					},
				],
			},
		},
		DOB: {
			entry: [
				assign({
					exemptionsProgress: context => (5 / 16) * 100,
				}),
			],
			on: {
				back: {
					target: "US_CITIZEN",
					actions: assign({
						dobValue: context => undefined,
					}),
				},
				next: [
					{
						target: "NON_AGREEMENT_CONFIRMATION",
						guard: ({ context, event }) =>
							moment().diff(context.dobValue, "years") >= 65,
					},
					{
						target: "RARE_SITUATIONS",
					},
				],
			},
		},
		RARE_SITUATIONS: {
			entry: [
				assign({
					exemptionsProgress: context => (6 / 16) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "DOB",
						actions: assign({
							hasDisability: context => undefined,
							specialFieldsValue: ({ context, event }) => ({
								...context.specialFieldsValue,
								is_widow: undefined,
								is_blind: undefined,
								is_disabled_first_responder_or_surviving_spouse_of_a_first_responder:
									undefined,
								is_surviving_spouse_of_a_first_responder_who_died_in_the_line_of_duty:
									undefined,
								is_a_disabled_veteran_aged_65_or_older_or_a_surviving_spouse_of_a_disabled_veteran:
									undefined,
								is_a_disabled_veteran_confined_to_a_wheelchair: undefined,
								is_a_totally_and_permanently_disabled_veteran_or_a_surviving_spouse_of_such_a_veteran:
									undefined,
								is_a_surviving_spouse_of_a_veteran_who_died_while_on_active_duty:
									undefined,
								is_active_duty_personnel: undefined,
							}),
						}),
					},
				],
				next: [
					{
						target: "NON_AGREEMENT_CONFIRMATION",
						guard: ({ context, event }) =>
							context.hasDisability === true ||
							context.specialFieldsValue?.is_widow === true ||
							context.specialFieldsValue?.is_blind === true ||
							context.specialFieldsValue
								?.is_disabled_first_responder_or_surviving_spouse_of_a_first_responder ===
								true ||
							context.specialFieldsValue
								?.is_surviving_spouse_of_a_first_responder_who_died_in_the_line_of_duty ===
								true ||
							context.specialFieldsValue
								?.is_a_disabled_veteran_aged_65_or_older_or_a_surviving_spouse_of_a_disabled_veteran ===
								true ||
							context.specialFieldsValue
								?.is_a_disabled_veteran_confined_to_a_wheelchair === true ||
							context.specialFieldsValue
								?.is_a_totally_and_permanently_disabled_veteran_or_a_surviving_spouse_of_such_a_veteran ===
								true ||
							context.specialFieldsValue
								?.is_a_surviving_spouse_of_a_veteran_who_died_while_on_active_duty ===
								true,
					},
					{
						target: "MOVE_IN_DATE",
					},
				],
			},
		},
		MOVE_IN_DATE: {
			entry: [
				assign({
					exemptionsProgress: context => (7 / 16) * 100,
				}),
			],
			on: {
				back: {
					target: "RARE_SITUATIONS",
					actions: assign({
						moveInDateValue: context => undefined,
					}),
				},
				next: {
					target: "SSN_TIN",
				},
			},
		},
		SSN_TIN: {
			entry: [
				assign({
					exemptionsProgress: context => (8 / 16) * 100,
				}),
			],
			on: {
				back: {
					target: "MOVE_IN_DATE",
					actions: assign({
						owner1SsnTin: context => undefined,
					}),
				},
				next: {
					target: "VEHICLE_REGISTRATIONS",
				},
			},
		},
		VEHICLE_REGISTRATIONS: {
			entry: [
				assign({
					exemptionsProgress: context => (8.25 / 13) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "SSN_TIN",
						actions: assign({
							specialFieldsValue: ({ context, event }) => ({
								...context.specialFieldsValue,
								vehicle_registrations: undefined,
							}),
						}),
					},
				],
				next: {
					target: "CURRENT_EMPLOYER",
				},
			},
		},
		CURRENT_EMPLOYER: {
			entry: [
				assign({
					exemptionsProgress: context => (8.5 / 13) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "VEHICLE_REGISTRATIONS",
						actions: assign({
							specialFieldsValue: ({ context, event }) => ({
								...context.specialFieldsValue,
								current_employer: undefined,
							}),
						}),
					},
				],
				next: {
					target: "PREVIOUS_TAX_RETURN_ADDRESS",
				},
			},
		},
		PREVIOUS_TAX_RETURN_ADDRESS: {
			entry: [
				assign({
					exemptionsProgress: context => (8.75 / 13) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "CURRENT_EMPLOYER",
						actions: assign({
							specialFieldsValue: ({ context, event }) => ({
								...context.specialFieldsValue,
								previous_tax_return_address: undefined,
							}),
						}),
					},
				],
				next: {
					target: "MARITAL_STATUS",
				},
			},
		},
		MARITAL_STATUS: {
			entry: [
				assign({
					exemptionsProgress: context => (9 / 16) * 100,
				}),
			],
			on: {
				back: {
					target: "SSN_TIN",
					actions: assign({
						maritalStatusValue: context => undefined,
					}),
				},
				next: {
					target: "OWNERS",
				},
			},
		},
		OWNERS: {
			entry: [
				assign({
					exemptionsProgress: context => (10 / 16) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "MARITAL_STATUS",
						actions: assign({
							ownersValue: context => undefined,
						}),
					},
				],
				next: [
					{
						target: "ADDITIONAL_OWNER",
						guard: ({ context, event }) =>
							context.ownersValue === "Other Persons",
					},
					{
						target: "DL_UPLOAD",
					},
				],
			},
		},
		ADDITIONAL_OWNER: {
			entry: [
				assign({
					exemptionsProgress: context => (11 / 16) * 100,
				}),
			],
			on: {
				back: {
					target: "OWNERS",
					actions: assign({
						additionalOwnerFirstNameValue: context => undefined,
						additionalOwnerLastNameValue: context => undefined,
						additionalOwnerDobValue: context => undefined,
						owner2SsnTin: context => undefined,
					}),
				},
				next: [
					{
						target: "OWNERSHIP_PERCENT",
						guard: ({ context, event }) =>
							context.maritalStatusValue !== "Married",
					},
					{
						target: "DL_UPLOAD",
						actions: assign({
							ownerOneValue: context => 100,
							ownerTwoValue: context => 100,
						}),
					},
				],
			},
		},
		OWNERSHIP_PERCENT: {
			entry: [
				assign({
					exemptionsProgress: context => (12 / 16) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "ADDITIONAL_OWNER",
						guard: ({ context, event }) =>
							context.maritalStatusValue !== "Married",
						actions: assign({
							ownerOneValue: context => undefined,
							ownerTwoValue: context => undefined,
						}),
					},
					{
						target: "OWNERS",
						actions: assign({
							ownerOneValue: context => undefined,
							ownerTwoValue: context => undefined,
						}),
					},
				],
				next: [
					{
						target: "DL_UPLOAD",
					},
				],
			},
		},
		DL_UPLOAD: {
			entry: [
				assign({
					exemptionsProgress: context => (13 / 16) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "OWNERS",
						guard: ({ context, event }) => commonGuards.isSoleOwner(context),
						actions: assign({
							ownerDlFilesToUpload: context => [],
							ownerDlExemptValue: context => undefined,
						}),
					},
					{
						target: "ADDITIONAL_OWNER",
						guard: ({ context, event }) =>
							context.maritalStatusValue === "Married",
						actions: assign({
							ownerDlFilesToUpload: context => [],
							ownerDlExemptValue: context => undefined,
						}),
					},
					{
						target: "OWNERSHIP_PERCENT",
						actions: assign({
							ownerDlFilesToUpload: context => [],
							ownerDlExemptValue: context => undefined,
						}),
					},
				],
				next: [
					{
						target: "DL2_UPLOAD",
						guard: ({ context, event }) =>
							commonGuards.hasAdditionalOwners(context),
					},
					{
						target: "AGREEMENT",
					},
				],
			},
		},
		DL2_UPLOAD: {
			entry: [
				assign({
					exemptionsProgress: context => (14 / 16) * 100,
				}),
			],
			on: {
				back: {
					target: "DL_UPLOAD",
					actions: assign({
						owner2DlFilesToUpload: context => [],
						owner2DlExemptValue: context => undefined,
					}),
				},
				next: {
					target: "AGREEMENT",
				},
			},
		},
		AGREEMENT: {
			entry: [
				assign({
					exemptionsProgress: context => (15 / 16) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "DL2_UPLOAD",
						guard: ({ context, event }) => !commonGuards.isSoleOwner(context),
					},
					{
						target: "DL_UPLOAD",
					},
				],
				next: {
					target: "MAIN_CONFIRMATION",
				},
			},
		},
		MAIN_CONFIRMATION: {
			entry: [
				assign({
					exemptionsProgress: context => 100,
				}),
			],
			on: {
				next: {
					target: "MAIN_CONFIRMATION",
				},
			},
		},
	},
	on: {
		update_context: {
			actions: "updateContext",
		},
		restart: {
			target: ".INIT",
		},
	},
});
